/** @jsxImportSource react */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Item } from '../types';
import { formatGBP } from '../utils/currency';
import './MyItems.css';

// Base64 encoded simple SVG placeholder
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';



const MyItems: React.FC = () => {
  const { user } = useAuth();
  const { getUserItems, isUsingSupabaseData, deleteItem } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'available' | 'rented'>('available');

  useEffect(() => {
    const loadUserItems = async () => {
      if (user) {
        setLoading(true);
        try {
          const userItems = await getUserItems(user.id);
          
          // Always use Supabase data when connected, even if empty
          setItems(userItems);
        } catch (error) {
          console.error('Error loading user items:', error);
          // Fall back to empty array on error
          setItems([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserItems();
  }, [user, getUserItems]);

  // Handle navigation state updates (for items added/edited/deleted from other pages)
  useEffect(() => {
    if (location.state) {
      const state = location.state as any;
      
      if (state.newItem) {
        // Add new item to the list
        setItems(prev => [...prev, state.newItem]);
        // Clear the state
        navigate(location.pathname, { replace: true, state: {} });
      } else if (state.updatedItem) {
        // Update existing item in the list
        setItems(prev => prev.map(item => 
          item.id === state.updatedItem.id ? state.updatedItem : item
        ));
        // Clear the state
        navigate(location.pathname, { replace: true, state: {} });
      } else if (state.deletedItemId) {
        // Remove deleted item from the list
        setItems(prev => prev.filter(item => item.id !== state.deletedItemId));
        // Clear the state
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, navigate, location.pathname]);

  const isCurrentlyRented = (item: Item): boolean => {
    if (!item.rentalPeriods || item.rentalPeriods.length === 0) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return item.rentalPeriods.some(period => {
      if (period.returned) return false;
      
      const startDate = new Date(period.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(period.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      return today >= startDate && today <= endDate;
    });
  };

  const isOverdue = (item: Item): boolean => {
    if (!item.rentalPeriods || item.rentalPeriods.length === 0) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return item.rentalPeriods.some(period => {
      if (period.returned) return false;
      
      const endDate = new Date(period.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      return today > endDate;
    });
  };

  const filteredItems = items.filter(item => {
    if (activeTab === 'available') {
      return !isCurrentlyRented(item);
    } else {
      return isCurrentlyRented(item);
    }
  });

  const handleAddItem = () => {
    navigate('/add-item');
  };

  const handleEditItem = (item: Item) => {
    navigate(`/edit-item/${item.id}`);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const success = await deleteItem(itemId);
        if (success) {
          // Remove from local state
          setItems(prev => prev.filter(item => item.id !== itemId));
          console.log('Item deleted successfully');
        } else {
          alert('Failed to delete item. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  if (!user) {
    return <div>Please log in to view your items.</div>;
  }

  if (loading) {
    return (
      <div className="my-items">
        <div className="my-items-header">
          <h1>My Items</h1>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading your items...
        </div>
      </div>
    );
  }

  return (
    <div className="my-items">
      <div className="my-items-header">
        <h1>My Items</h1>
        <button 
          className="add-item-button"
          onClick={handleAddItem}
        >
          Add New Item
        </button>
      </div>

      <div className="items-tabs">
        <button
          className={`tab-button ${activeTab === 'available' ? 'active' : ''}`}
          onClick={() => setActiveTab('available')}
        >
          Available ({items.filter(item => !isCurrentlyRented(item)).length})
        </button>
        <button
          className={`tab-button ${activeTab === 'rented' ? 'active' : ''}`}
          onClick={() => setActiveTab('rented')}
        >
          Currently Rented ({items.filter(item => isCurrentlyRented(item)).length})
        </button>
      </div>

      <div className="items-grid">
        {filteredItems.map(item => (
          <div 
            key={item.id} 
            className="item-card clickable-item"
            onClick={() => handleEditItem(item)}
          >
            <div className="item-image-container">

              {isCurrentlyRented(item) && (
                <div className={`rental-status ${isOverdue(item) ? 'overdue' : 'rented'}`}>
                  {isOverdue(item) 
                    ? `Overdue (due ${item.rentalPeriods.find(p => !p.returned)?.endDate ? new Date(item.rentalPeriods.find(p => !p.returned)!.endDate).toLocaleDateString() : ''})`
                    : `Rented until ${item.rentalPeriods.find(p => !p.returned)?.endDate ? new Date(item.rentalPeriods.find(p => !p.returned)!.endDate).toLocaleDateString() : ''}`
                  }
                </div>
              )}
              <img 
                src={item.images[0] || PLACEHOLDER_IMAGE} 
                alt={`${item.name}`}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = e.currentTarget;
                  if (img.src !== PLACEHOLDER_IMAGE) {
                    img.onerror = null; // Prevent infinite loop
                    img.src = PLACEHOLDER_IMAGE;
                  }
                }}
                className="item-image"
              />
            </div>
            <div className="item-info">
              <h3>{item.name}</h3>
              <div className="item-pricing">
                <p className="daily-rate">£{item.dailyRateGBP.toFixed(2)}/day</p>
              </div>
              <p>{item.description}</p>
              <div className="item-actions">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditItem(item);
                  }}
                >
                  Edit
                </button>
                <button 
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(item.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyItems; 